import { cn } from "@-/com.npmjs.cn";
import {
  Platform,
  TextInput,
  type TextInputProps,
  type TextStyle,
} from "@-/com.npmjs.react-native";

export type InputProps = TextInputProps & React.RefAttributes<TextInput>;

/**
 * Single-line native: slightly smaller top inset than bottom shifts the layout rect up so
 * glyphs sit closer to optical center (symmetric padding keeps the inner rect centered
 * but RN + font metrics still read as vertically low).
 */
const nativeSingleLineTextStyleBase = {
  fontSize: 16,
  lineHeight: 22,
  paddingBottom: 6,
  paddingTop: 2,
} satisfies TextStyle;

function getSingleLineTextStyle(multiline: boolean | undefined): TextStyle | undefined {
  if (multiline === true || Platform.OS === "web") {
    return undefined;
  }

  if (Platform.OS === "ios") {
    return { ...nativeSingleLineTextStyleBase, verticalAlign: "middle" };
  }

  return nativeSingleLineTextStyleBase;
}

export function Input({ className, multiline, style, ...props }: InputProps) {
  const singleLineTextStyle = getSingleLineTextStyle(multiline);

  return (
    <TextInput
      {...(Platform.OS === "android"
        ? {
            includeFontPadding: false,
            textAlignVertical: multiline ? ("top" as const) : ("center" as const),
          }
        : {})}
      className={cn(
        "w-full min-w-0 rounded-xs border border-input bg-background px-3 font-sans text-base text-foreground shadow-sm shadow-black/5 dark:bg-input/30",
        Platform.select({
          default:
            multiline === true ? "h-12 self-stretch py-2 leading-6" : "h-12 self-stretch leading-5",
          ios: multiline === true ? "h-12 self-stretch py-2 leading-6" : "h-12 self-stretch",
          web:
            multiline === true
              ? "flex min-h-12 w-full flex-row items-start self-stretch py-2 leading-6"
              : "flex h-12 flex-row items-center self-stretch py-0 leading-12 sm:h-9 sm:leading-9",
        }),
        props.editable === false &&
          cn(
            "opacity-50",
            Platform.select({
              web: "disabled:pointer-events-none disabled:cursor-not-allowed",
            }),
          ),
        Platform.select({
          web: cn(
            "transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground md:text-sm",
            "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
            "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          ),
        }),
        className,
      )}
      multiline={multiline}
      // react-native-web paints ::placeholder from its own placeholderTextColor variable, which
      // `placeholder:*` classes cannot reach, so web takes the accent colour too.
      placeholderTextColorClassName={
        Platform.OS === "web" ? "accent-muted-foreground" : "accent-muted-foreground/50"
      }
      style={[singleLineTextStyle, style]}
      {...props}
    />
  );
}
